import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@cloudscape-design/components/button';
import { trackEvents } from '@/lib/events';
import { csvToBlob, toCsv } from './csvStream';
import type { ColumnDef } from './types';
import { serverDate } from '@etiquetachef/ui-shared/online';

export type ExportButtonProps<T> = {
  data: readonly T[];
  columns: readonly ColumnDef<T>[];
  /** Identificador da fonte para o evento PostHog (ex.: "finance/topDays"). */
  source: string;
  /** Nome do arquivo sem extensão. */
  filename?: string;
  disabled?: boolean;
};

/** `<nome>-AAAA-MM-DD.csv`, com a data UTC do navegador. */
function buildFilename(name: string): string {
  const stamp = serverDate().toISOString().slice(0, 10);
  return `${name}-${stamp}.csv`;
}

/**
 * Botão genérico de export CSV. Click → toCsv → Blob → download
 * via createObjectURL. Emite `bi_csv_exported` no PostHog com
 * source + nº de linhas. CSV neutraliza fórmula (`csvEscape`, #795).
 * O PDF mensal não passa por aqui: mora em `features/reports/ReportsPage.tsx`.
 */
export function ExportButton<T>({
  data,
  columns,
  source,
  filename,
  disabled,
}: ExportButtonProps<T>): React.JSX.Element {
  const { t } = useTranslation();
  const handleClick = (): void => {
    const csv = toCsv(data, columns);
    const blob = csvToBlob(csv);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = filename ?? source.replace(/[^a-z0-9-]+/gi, '-');
    a.download = buildFilename(baseName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackEvents.biCsvExported(source, data.length);
  };
  return (
    <Button
      iconName="download"
      disabled={disabled === true || data.length === 0}
      onClick={handleClick}
    >
      {t('bi.export.csv')}
    </Button>
  );
}
